import { useEffect, useMemo, useState } from "react";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { useToast } from "../components/Toast.js";
import { useConfirm } from "../components/ConfirmDialog.js";

// Mirrors services/hub/src/hubConfig/service.ts (HubConfigStatus / ManagedConfigStatus).
type ConfigStatusValue = "db" | "env" | "default";
type ConfigCategory = "operations" | "deployment" | "email";

type ConfigInputKind =
  | { kind: "text" }
  | { kind: "boolean" }
  | { kind: "number" }
  | { kind: "select"; options: readonly string[] };

type ManagedConfigStatus = {
  key: string;
  envVar: string;
  label: string;
  helpText: string;
  category: ConfigCategory;
  requiresRestart: boolean;
  input: ConfigInputKind;
  status: ConfigStatusValue;
  currentValue: string | null;
  updatedAt: string | null;
  updatedByEmail: string | null;
};

type HubConfigResponse = {
  config: ManagedConfigStatus[];
};

/**
 * ADR-0060: `applied: true` means the new value is live in the Hub's process
 * right now. `applied: false` means either requiresRestart is true OR the
 * live-refresh failed; the user should restart.
 */
type UpdateConfigResponse = { ok: true; requiresRestart: boolean; applied: boolean };

const CATEGORY_LABELS: Record<ConfigCategory, string> = {
  email: "Email",
  deployment: "Deployment",
  operations: "Operations"
};

const CATEGORY_ORDER: ConfigCategory[] = ["email", "deployment", "operations"];

function statusPill(status: ConfigStatusValue) {
  switch (status) {
    case "db":
      return <span className="pill pillOk">In database</span>;
    case "env":
      return <span className="pill">From .env (fallback)</span>;
    case "default":
      return <span className="pill pillWarn">Using default</span>;
  }
}

function formatUpdatedAt(updatedAt: string | null, updatedByEmail: string | null): string {
  if (!updatedAt) return "—";
  const date = new Date(updatedAt);
  const dateStr = isNaN(date.getTime()) ? updatedAt : date.toLocaleString();
  return updatedByEmail ? `${dateStr} by ${updatedByEmail}` : dateStr;
}

export function AdminHubConfig() {
  const [data, setData] = useState<HubConfigResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Per-row draft state, keyed by config key.
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [restartBanner, setRestartBanner] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const toast = useToast();
  const confirm = useConfirm();

  async function refresh(opts?: { silent?: boolean }) {
    const silent = opts?.silent === true;
    if (!silent) setLoading(true);
    setError(null);
    try {
      const res = await apiFetch<HubConfigResponse>("/api/admin/hub-config");
      setData(res);
      // Pre-populate drafts with the current value for each row.
      const initialDrafts: Record<string, string> = {};
      for (const c of res.config) {
        initialDrafts[c.key] = c.currentValue ?? "";
      }
      setDrafts((prev) => {
        // Preserve any in-flight edits the user has typed but not saved.
        const merged = { ...initialDrafts };
        for (const k of Object.keys(prev)) {
          if (prev[k] !== undefined) merged[k] = prev[k];
        }
        return merged;
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (!silent) setLoading(false);
    }
  }

  async function saveConfig(key: string) {
    const value = drafts[key];
    if (!value || value.trim().length === 0) {
      setRowErrors((prev) => ({ ...prev, [key]: "Value is required." }));
      return;
    }
    setSaving((prev) => ({ ...prev, [key]: true }));
    setRowErrors((prev) => ({ ...prev, [key]: "" }));
    try {
      const res = await apiFetch<UpdateConfigResponse>(
        `/api/admin/hub-config/${encodeURIComponent(key)}`,
        { method: "PUT", body: { value } }
      );
      // ADR-0060: show the banner only when the value is NOT live yet.
      if (!res.applied) setRestartBanner(true);
      // Wipe the in-progress draft for this key so refresh() repopulates with the saved value.
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      await refresh({ silent: true });
    } catch (e) {
      setRowErrors((prev) => ({ ...prev, [key]: e instanceof Error ? e.message : String(e) }));
    } finally {
      setSaving((prev) => ({ ...prev, [key]: false }));
    }
  }

  async function handleRestart() {
    const ok = await confirm.confirm({
      title: "Restart Hub now?",
      detail:
        "Hub will exit and systemd will respawn it. Active requests are dropped; downtime is typically 5–15 seconds.",
      confirmLabel: "Restart Hub",
      cancelLabel: "Cancel"
    });
    if (!ok) return;
    setRestarting(true);
    try {
      await apiFetch("/api/admin/hub-config/restart", { method: "POST" });
    } catch (e) {
      // 409 = hub refused (e.g. not supervised by systemd in dev). Don't poll
      // /health — the hub is still up and the user needs the message verbatim.
      if (e instanceof ApiRequestError && e.status === 409) {
        setRestarting(false);
        toast.push({ kind: "error", message: e.message });
        return;
      }
      // Any other error (incl. network): the hub may have exited before the
      // response flushed. Fall through to the health poll.
    }
    // Poll /health until the new process answers, with a hard cap.
    const deadline = Date.now() + 60_000;
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 1_000));
      try {
        await apiFetch<{ ok: boolean }>("/api/health");
        setRestarting(false);
        setRestartBanner(false);
        toast.push({ kind: "success", message: "Hub restarted." });
        await refresh({ silent: true });
        return;
      } catch {
        // still down; keep polling
      }
    }
    setRestarting(false);
    toast.push({
      kind: "error",
      message: "Hub did not come back within 60s. Check service status on the host."
    });
  }

  useEffect(() => {
    void refresh();
  }, []);

  const configByCategory = useMemo(() => {
    const groups: Record<ConfigCategory, ManagedConfigStatus[]> = {
      email: [],
      deployment: [],
      operations: []
    };
    for (const c of data?.config ?? []) {
      groups[c.category].push(c);
    }
    return groups;
  }, [data]);

  function renderInput(c: ManagedConfigStatus) {
    const draftValue = drafts[c.key] ?? "";
    const isDirty = draftValue !== (c.currentValue ?? "");
    const disabled = saving[c.key] === true;

    if (c.input.kind === "select") {
      return (
        <select
          className="select"
          value={draftValue}
          disabled={disabled}
          aria-label={`${c.label} value`}
          onChange={(e) => setDrafts((prev) => ({ ...prev, [c.key]: e.target.value }))}
        >
          {!c.input.options.includes(draftValue) && draftValue !== "" && (
            <option value={draftValue}>{draftValue} (current)</option>
          )}
          {c.input.options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      );
    }

    if (c.input.kind === "boolean") {
      return (
        <select
          className="select"
          value={draftValue}
          disabled={disabled}
          aria-label={`${c.label} value`}
          onChange={(e) => setDrafts((prev) => ({ ...prev, [c.key]: e.target.value }))}
        >
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      );
    }

    return (
      <input
        type={c.input.kind === "number" ? "number" : "text"}
        className="input"
        value={draftValue}
        disabled={disabled}
        autoComplete="off"
        aria-label={`${c.label} value`}
        onChange={(e) => setDrafts((prev) => ({ ...prev, [c.key]: e.target.value }))}
      />
    );
  }

  function renderRow(c: ManagedConfigStatus) {
    const draftValue = drafts[c.key] ?? "";
    const isDirty = draftValue !== (c.currentValue ?? "");
    return (
      <tr key={c.key}>
        <td>
          <div style={{ fontWeight: 800 }}>{c.label}</div>
          <div className="muted mono" style={{ fontSize: 12 }}>
            {c.envVar}
          </div>
          <div className="help" style={{ marginTop: 4 }}>
            {c.helpText}
          </div>
        </td>
        <td>
          {statusPill(c.status)}
          <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
            {formatUpdatedAt(c.updatedAt, c.updatedByEmail)}
          </div>
        </td>
        <td>
          {renderInput(c)}
          {rowErrors[c.key] && <div className="help">{rowErrors[c.key]}</div>}
        </td>
        <td>
          <button
            className="btn btnPrimary btnSm"
            type="button"
            disabled={(!isDirty && c.status === "db") || saving[c.key] === true}
            onClick={() => void saveConfig(c.key)}
          >
            {saving[c.key] ? "Saving…" : "Save"}
          </button>
        </td>
      </tr>
    );
  }

  return (
    <div>
      <PageLayout
        title="Hub Config"
        description="Operator-editable non-secret configuration. Values are stored in hub_config and override .env at startup. For credentials and secrets, see Atlas Vault."
      >
        {error && <ErrorState detail={error} />}
        {loading && <LoadingState />}

        {restartBanner && (
          <div
            className="card"
            role="status"
            style={{ marginTop: 12, borderLeft: "4px solid var(--warn-text, #c97500)" }}
          >
            <div style={{ fontWeight: 800, marginBottom: 6 }}>Restart required</div>
            <div className="help">
              One or more configuration values were updated. Restart Hub for the new values to take effect.
            </div>
            <div className="actions" style={{ marginTop: 10 }}>
              <button
                className="btn btnPrimary"
                onClick={() => void handleRestart()}
                disabled={restarting}
              >
                {restarting ? "Restarting…" : "Restart Hub"}
              </button>
            </div>
          </div>
        )}

        {data &&
          CATEGORY_ORDER.map((cat) => {
            const rows = configByCategory[cat];
            if (!rows || rows.length === 0) return null;
            return (
              <div key={cat} className="card tableWrap" style={{ marginTop: 12 }}>
                <div style={{ padding: "10px 12px", fontWeight: 800 }}>{CATEGORY_LABELS[cat]}</div>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Setting</th>
                      <th style={{ width: 200 }}>Status</th>
                      <th style={{ width: "30%" }}>Value</th>
                      <th style={{ width: 120 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>{rows.map((c) => renderRow(c))}</tbody>
                </table>
              </div>
            );
          })}

        <div className="card" style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 800, marginBottom: 6 }}>About Hub Config</div>
          <div className="help">
            Hub Config holds operator-editable, non-sensitive operational settings. The Hub boot
            loader reads these values from Postgres and writes them into <span className="mono">process.env</span>{" "}
            before the rest of Hub starts. Saves are persisted immediately, but settings marked{" "}
            <i>requires restart</i> only take effect on the next boot — use the <b>Restart Hub</b> button
            in the banner above (or restart the service on the host) to apply them.
          </div>
          <div className="help" style={{ marginTop: 6 }}>
            For passwords, API keys, and other secrets, use <b>Atlas Vault</b> (the sibling page).
          </div>
        </div>
      </PageLayout>
    </div>
  );
}
